import React, { useEffect, useState, useRef } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Modal,
  IconButton,
  alpha,
  Checkbox,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import jwtAxios from '@projectK/services/auth/JWT';
import AppGridContainer from '@projectK/components/AppGridContainer';
import AppTextField from '@projectK/components/AppTextField';
import IntlMessages from '@projectK/helpers/IntlMessages';
import CompanyTheme from '@projectK/constants/CompanyTheme';
import AppLoader from '@projectK/components/AppLoader';
import PropTypes from 'prop-types';
import AppCard from '@projectK/components/AppCard';
import CloseIcon from '@mui/icons-material/Close';
import { Fonts } from '@projectK/constants/AppEnums';
const customScrollbarStyle = {
  // Define the styles for the scrollbar
  '&::-webkit-scrollbar': {
    width: '8px',
  },

  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#44444461', // Lighter black color
    borderRadius: '8px', // Curved border
  },

  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#666', // Lighter black color on hover
  },
};

const CONTAINER_IDS = [
  '#add-card-number-container',
  '#add-card-expiration-container',
  '#add-card-cvv-container',
];

const clearContainers = () => {
  CONTAINER_IDS.forEach((id) => {
    const el = document.querySelector(id);
    if (el) el.innerHTML = '';
  });
};

const validationSchema = yup.object({
  address: yup
    .string()
    .max(255, () => <IntlMessages id='validation.addressTooLong' />)
    .required(<IntlMessages id='validation.addressRequired' />),
  state: yup
    .string()
    .max(50, () => <IntlMessages id='validation.stateMax' />)
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.stateFormat' />
    ))
    .required(<IntlMessages id='validation.stateRequired' />),
  city: yup
    .string()
    .max(50, () => <IntlMessages id='validation.cityMax' />)
    .matches(/^[A-Za-z]+(?:[\s-][A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.cityFormat' />
    ))
    .required(<IntlMessages id='validation.cityRequired' />),
  country: yup
    .string()
    .max(50, () => <IntlMessages id='validation.countryMax' />)
    .matches(/^[A-Za-z]+(?:\s[A-Za-z]+)*$/, () => (
      <IntlMessages id='validation.countryFormat' />
    ))
    .required(<IntlMessages id='validation.countryRequired' />),
  zip_code: yup
    .string()
    .matches(/^(\d{1,8}|[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d)$/, () => (
      <IntlMessages id='validation.zipCodeFormat' />
    ))
    .required(<IntlMessages id='validation.zipCodeRequired' />),
});

const fieldStyle = {
  padding: '16.5px 14px',
  border: '1px solid rgb(204, 204, 204)',
  borderRadius: '10px',
  margin: 0,
  height: '1.4375em',
  background: '#fafafa',
  fontsize: '14px',
  font: 'inherit',
  letterSpacing: 'inherit',
  color: 'currentColor',
  boxSizing: 'content-box',
  display: 'block',
};

const AddCardForm = ({
  isOpen,
  onClose,
  onSuccess,
  fetchError,
  showMessage,
  userId,
}) => {
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tilled, setTilled] = useState(null);
  const [isFormValid, setIsFormValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [states, setStates] = useState([]);
  const [country, setCountry] = useState('');
  const [setAsDefault, setSetAsDefault] = useState(true);
  const tilledFormRef = useRef(null);
  const companyTheme = CompanyTheme();

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    const load = async () => {
      try {
        const [paymentRes, userRes] = await Promise.all([
          jwtAxios.get('/customer-payment'),
          userId
            ? jwtAxios.get(`/users/${userId}`)
            : Promise.resolve({ data: {} }),
        ]);
        const list = paymentRes.data?.list;
        const pm = Array.isArray(list) && list.length > 0 ? list[0] : list;
        const profile = userRes?.data || {};
        setPaymentMethod(pm || null);
        setUserProfile(profile);
        setCountry(profile.country ?? '');
      } catch (e) {
        fetchError(
          e?.response?.data?.message || e?.message || 'Failed to load form',
        );
        onClose();
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen, userId, onClose, fetchError]);

  useEffect(() => {
    if (country) {
      jwtAxios
        .get('/states', { params: { country, perPage: 'all' } })
        .then((res) => setStates(res.data?.list ?? []))
        .catch(() => setStates([]));
    } else {
      setStates([]);
    }
  }, [country]);

  useEffect(() => {
    let tilledForm = null;
    if (!isOpen || !paymentMethod || loading || !window.Tilled) {
      return () => {};
    }
    const TILLED_PUBLIC_KEY =
      paymentMethod.payment_mode === 'live'
        ? paymentMethod.live_public_key
        : paymentMethod.test_public_key;
    const TILLED_ACCOUNT_ID =
      paymentMethod.payment_mode === 'live'
        ? paymentMethod.live_merchant_id
        : paymentMethod.test_merchant_id;
    const TILLED_LIVE = paymentMethod.payment_mode === 'live';

    const run = async () => {
      try {
        clearContainers();
        const tilledInstance = new window.Tilled(
          TILLED_PUBLIC_KEY,
          TILLED_ACCOUNT_ID,
          { sandbox: !TILLED_LIVE },
        );
        setTilled(tilledInstance);
        tilledForm = await tilledInstance.form({ payment_method_type: 'card' });
        tilledFormRef.current = tilledForm;

        const fieldOptions = {
          styles: {
            base: {
              fontFamily: 'Helvetica Neue, Arial, sans-serif',
              color: '#304166',
              fontWeight: '400',
              fontSize: '14px',
              '::placeholder': { color: '#94A3B8' },
            },
            invalid: { ':hover': { textDecoration: 'underline dotted red' } },
            valid: { color: '#00BDA5' },
          },
        };

        const cardNumberField = tilledForm
          .createField('cardNumber', {
            ...fieldOptions,
            placeholder: 'Card Number',
          })
          .inject('#add-card-number-container');
        const cardExpiryField = tilledForm
          .createField('cardExpiry', {
            ...fieldOptions,
            placeholder: 'MM / YY',
          })
          .inject('#add-card-expiration-container');
        const cardCvvField = tilledForm
          .createField('cardCvv', { ...fieldOptions, placeholder: 'CVV' })
          .inject('#add-card-cvv-container');

        const checkFormValidity = () => {
          setIsFormValid(
            cardNumberField._valid &&
              cardExpiryField._valid &&
              cardCvvField._valid,
          );
        };
        cardNumberField.on('change', checkFormValidity);
        cardExpiryField.on('change', checkFormValidity);
        cardCvvField.on('change', checkFormValidity);

        await tilledForm.build();
      } catch (error) {
        console.error('Failed to load Tilled script:', error);
        fetchError(error?.message || 'Failed to load payment form');
      }
    };
    run();
    return () => {
      setTilled(null);
      if (tilledForm?.teardown) tilledForm.teardown();
      tilledFormRef.current = null;
      clearContainers();
    };
  }, [isOpen, paymentMethod, loading, fetchError]);

  const handleClose = () => {
    if (tilledFormRef.current?.teardown) tilledFormRef.current.teardown();
    clearContainers();
    onClose();
  };

  const initialValues = {
    address: userProfile?.address ?? '',
    state: userProfile?.state ?? '',
    city: userProfile?.city ?? '',
    zip_code: (userProfile?.zip_code ?? '') + '',
    country: userProfile?.country ?? '',
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <Box sx={{ py: 2 }}>
        <AppLoader />
      </Box>
    );
  }

  if (!paymentMethod) {
    return (
      <Typography color='text.secondary'>
        Payment configuration not available. Try again later.
      </Typography>
    );
  }

  return (
    <Modal open={isOpen} onClose={() => handleClose()}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: '8px',
          boxShadow: 24,
          p: 4,
          minWidth: 300,
          maxWidth: 600,
          maxHeight: 'calc(100vh - 200px)',
          overflow: 'auto',
          ...customScrollbarStyle,
        }}
      >
        <AppCard
          title={
            <Box
              sx={{
                fontSize: 16,
                fontWeight: Fonts?.BOLD,
                color: companyTheme ? companyTheme?.primary?.main : '#060808',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                sx={{
                  fontWeight: Fonts.BOLD,
                  fontSize: 18,
                }}
              >
                Add Card
              </Typography>

              <IconButton onClick={() => handleClose()} aria-label='close'>
                <CloseIcon />
              </IconButton>
            </Box>
          }
        >
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              if (!tilled) {
                fetchError('Payment form not ready.');
                return;
              }
              if (!isFormValid) {
                fetchError(
                  'Please complete card fields until they turn green.',
                );
                return;
              }
              setSubmitting(true);
              try {
                const billing_details = {
                  name: userProfile
                    ? `${userProfile.first_name || ''} ${
                        userProfile.last_name || ''
                      }`.trim()
                    : 'Cardholder',
                  address: {
                    street: values.address,
                    city: values.city,
                    state: values.state,
                    zip: String(values.zip_code),
                    country: values.country,
                  },
                  email: userProfile?.email || undefined,
                  phone: userProfile?.user_mobile || undefined,
                };
                const result = await tilled.createPaymentMethod({
                  type: 'card',
                  billing_details,
                });
                if (result?.error) throw new Error(result.error.message);
                await jwtAxios.post(
                  `/subscriptionsPayments/wallet/cards/attach/${userId}`,
                  {
                    paymentMethodId: result.id,
                  },
                );
                if (setAsDefault) {
                  await jwtAxios.put(
                    `/subscriptionsPayments/wallet/cards/${result.id}/${userId}/default`,
                  );
                }
                showMessage('Card added successfully');
                onSuccess();
                handleClose();
              } catch (err) {
                fetchError(
                  err?.response?.data?.message || err?.response?.data?.error ||
                    err?.message ||
                    'Failed to add card',
                );
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ touched, errors, setFieldValue, values }) => (
              <Form>
                <AppGridContainer>
                  <Grid size={{ xs: 12, md: 12 }}>
                    <AppTextField
                      name='address'
                      value={values.address}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      label='Address'
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl sx={{ width: '100%', borderRadius: 50 }}>
                      <TextField
                        sx={{ width: '100%' }}
                        select
                        name='country'
                        value={values.country ?? country}
                        label='Country'
                        error={touched.country && Boolean(errors.country)}
                        helperText={touched.country && errors.country}
                        onChange={(e) => {
                          setCountry(e.target.value);
                          setFieldValue('country', e.target.value);
                        }}
                      >
                        <MenuItem value='us'>US</MenuItem>
                        <MenuItem value='ca'>CA</MenuItem>
                      </TextField>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Autocomplete
                      id='add-card-state-autocomplete'
                      options={states}
                      getOptionLabel={(option) =>
                        `${option.name} - ${option.abbreviation}`
                      }
                      value={
                        states?.find((s) => s.abbreviation === values.state) ||
                        null
                      }
                      onChange={(event, newValue) => {
                        setFieldValue(
                          'state',
                          newValue ? newValue.abbreviation : '',
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='State'
                          variant='outlined'
                          error={touched.state && Boolean(errors.state)}
                          helperText={touched.state && errors.state}
                        />
                      )}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AppTextField
                      name='city'
                      value={values.city}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      label='City'
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AppTextField
                      name='zip_code'
                      value={values.zip_code}
                      variant='outlined'
                      sx={{ width: '100%' }}
                      label='Zip Code'
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={setAsDefault}
                          onChange={(e) => setSetAsDefault(e.target.checked)}
                          color='primary'
                        />
                      }
                      label='Set as default payment method'
                    />
                  </Grid>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mt: 2,
                      ml: 1,
                      color: '#9d9d9d',
                      fontSize: '15px !important',
                    }}
                  >
                    <InfoOutlinedIcon
                      fontSize='small'
                      sx={{ mr: 2, color: '#000' }}
                    />
                    Please fill the below fields manually. Submit only after
                    they turn green.
                  </Box>

                  <Grid
                    size={{ xs: 12, md: 12 }}
                    style={{ paddingTop: '0 !important', paddingBottom: '0' }}
                  >
                    <div
                      id='add-card-number-container'
                      style={fieldStyle}
                      className='card-number-wrapper'
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    style={{
                      paddingTop: '0 !important',
                      paddingLeft: '30 !important',
                    }}
                  >
                    <div
                      id='add-card-expiration-container'
                      style={fieldStyle}
                      className='card-expiration-wrapper'
                    />
                  </Grid>
                  <Grid
                    size={{ xs: 12, md: 6 }}
                    style={{ paddingTop: '0 !important' }}
                  >
                    <div
                      id='add-card-cvv-container'
                      style={fieldStyle}
                      className='card-cvv-wrapper'
                    />
                  </Grid>
                </AppGridContainer>

                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mt: 2,
                    pt: 5,
                  }}
                >
                  <Button
                    type='submit'
                    variant='contained'
                    disabled={submitting || !isFormValid}
                    sx={{
                      position: 'relative',
                      width: '100%',
                      backgroundColor: companyTheme
                        ? companyTheme.primary.main
                        : '#2196f3',
                      '&:hover': {
                        backgroundColor: companyTheme
                          ? alpha(companyTheme.primary.main, 0.8) // Adjust the alpha value as needed
                          : alpha('#2196f3', 0.8),
                      },
                      // 👇 Add this block to control disabled appearance
                      '&.Mui-disabled': {
                        backgroundColor: companyTheme
                          ? companyTheme.primary.main
                          : '#2196f3',
                        color: '#fff', // Optional: keep text visible
                        opacity: 0.6, // Optional: slight dim to indicate disabled state
                      },
                    }}
                  >
                    Add card
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </AppCard>
      </Box>
    </Modal>
  );
};

AddCardForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  fetchError: PropTypes.func.isRequired,
  showMessage: PropTypes.func.isRequired,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default AddCardForm;
